import {
  useCallback,
  useContext,
  useEffect,
  useState,
  type MouseEvent,
} from "react";
import { useParams } from "react-router-dom";
import { FaFile, FaMapMarkerAlt } from "react-icons/fa";
import { DashboardContext } from "../../../../../contexts/Dashboard/DashboardContext";
import { LoadingContext } from "../../../../../contexts/Loading/LoadingContext";
import { api, errorTreatment } from "../../../../../services/api";
import LoadingGraphics from "../../../../Loading/LoadingGraphics";
import NumberProps from "../NumberProps";
import { AuthContext } from "../../../../../contexts/Authentication/AuthContext";

const GROUP_BY_STORAGE_KEY = "numbersDayFinishedDocuments_groupBy";

type GroupByOption = "documents" | "stops";

const getStoredGroupBy = (screenId: string | undefined): GroupByOption => {
  try {
    const key = screenId ? `${GROUP_BY_STORAGE_KEY}_${screenId}` : GROUP_BY_STORAGE_KEY;
    const stored = localStorage.getItem(key);
    if (stored === "documents" || stored === "stops") return stored;
  } catch {
    // ignore
  }
  return "documents";
};

const setStoredGroupBy = (value: GroupByOption, screenId: string | undefined) => {
  try {
    const key = screenId ? `${GROUP_BY_STORAGE_KEY}_${screenId}` : GROUP_BY_STORAGE_KEY;
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

export const NumbersDayFinishedDocuments = () => {
  const { id, shortToken, mode } = useParams<{
    id?: string;
    shortToken?: string;
    mode?: string;
  }>();
  const { changeVisibility } = useContext(LoadingContext);
  const { hasShortToken } = useContext(AuthContext);
  const {
    reloadComponent,
    componentProperty,
    generateRedirectUrl,
  } = useContext(DashboardContext);

  const [documents, setDocuments] = useState<{ count: number }>({ count: 0 });
  const [completeRequisition, setCompleteRequisition] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupByOption>(() =>
    getStoredGroupBy(id)
  );

  const baseRoute =
    hasShortToken
      ? `/documents?graphic_id=numbersDayFinishedDocuments&short_token=${shortToken}&screen_id=${id}&group_by=${groupBy}`
      : `/documents?graphic_id=numbersDayFinishedDocuments&screen_id=${id}&group_by=${groupBy}`;

  const requisition = useCallback(async () => {
    return api
      .get(baseRoute)
      .then((response: { data: { count: number } }) => {
        setDocuments(response.data);
      })
      .catch((error) => {
        errorTreatment(error);
      });
  }, [baseRoute]);

  useEffect(() => {
    setGroupBy((prev) => getStoredGroupBy(id));
  }, [id]);

  useEffect(() => {
    changeVisibility(true);
    const fetchData = async () => {
      try {
        await requisition();
      } finally {
        changeVisibility(false);
        setCompleteRequisition(true);
      }
    };
    fetchData();

    if (mode === "view") {
      const intervalId = setInterval(requisition, 60000);
      return () => clearInterval(intervalId);
    }
  }, [requisition, mode]);

  const handleGroupByChange = (next: GroupByOption) => {
    setGroupBy(next);
    setStoredGroupBy(next, id);
  };

  useEffect(() => {
    if (
      mode === "view" &&
      componentProperty({
        id: "numbersDayFinishedDocuments",
        property: "redirect",
      })
    ) {
      const numberEl = document.querySelector(
        "#numbers-day-finished-documents .number-item"
      );
      if (numberEl) {
        const handler = async () => {
          window.open(
            await generateRedirectUrl("numbersDayFinishedDocuments", {
              redirectTowerToIndex: true,
              date: true,
              dueRouteDate: "dateR",
              status_stop_id: "3,4,8,13",
            }),
            "_blank"
          );
        };
        numberEl.addEventListener("click", handler);
        return () => numberEl.removeEventListener("click", handler);
      }
    }
  }, [completeRequisition, mode, componentProperty, generateRedirectUrl]);

  const isEditMode = mode === "edit";

  const headerRight = isEditMode ? (
    <div
      className="header-agrupamento-trailing gap-2"
      style={{ fontSize: "1rem" }}
      aria-label="Agrupamento"
      onMouseDown={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
    >
      <FaFile
        aria-hidden
        style={{
          opacity: groupBy === "documents" ? 1 : 0.5,
          cursor: "pointer",
        }}
        onClick={() => handleGroupByChange("documents")}
      />
      <button
        type="button"
        role="switch"
        aria-checked={groupBy === "stops"}
        aria-label={
          groupBy === "stops" ? "Agrupar por paradas" : "Agrupar por documentos"
        }
        title={groupBy === "stops" ? "Paradas" : "Documentos"}
        onClick={() =>
          handleGroupByChange(groupBy === "documents" ? "stops" : "documents")
        }
        style={{
          width: 36,
          height: 20,
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.3)",
          background: groupBy === "stops" ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)",
          cursor: "pointer",
          padding: 0,
          position: "relative",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: groupBy === "documents" ? 2 : 18,
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: "#fff",
            transition: "left 0.2s ease",
          }}
        />
      </button>
      <FaMapMarkerAlt
        aria-hidden
        style={{
          opacity: groupBy === "stops" ? 1 : 0.5,
          cursor: "pointer",
        }}
        onClick={() => handleGroupByChange("stops")}
      />
    </div>
  ) : (
    <span style={{ fontSize: "1rem" }} aria-hidden>
      {groupBy === "documents" ? (
        <FaFile title="Documentos" />
      ) : (
        <FaMapMarkerAlt title="Paradas" />
      )}
    </span>
  );

  if (!completeRequisition || reloadComponent) {
    return <LoadingGraphics />;
  }

  return (
    <div id="numbers-day-finished-documents" className="w-100">
      <NumberProps
        value={documents}
        title="Documentos Finalizados"
        id="numbers-day-finished-documents"
        clickable={componentProperty({
          id: "numbersDayFinishedDocuments",
          property: "redirect",
        })}
        scale={12}
        headerRight={headerRight}
      />
    </div>
  );
};
